/**
 * The external dependencies.
 */
import React from 'react';
import styled from 'styled-components';
import moment from 'moment';
import * as R from 'ramda';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome'
import {faObjectGroup, faUser, faUsers, faUserClock, faUserAlt} from '@fortawesome/free-solid-svg-icons'
/**
 * The internal dependencies.
 */
import {getWorkstepLabel, getWorkstepName} from "../../../common/utilities";
import {ts_translate} from "../../../common/clientTranslate";

const Wrapper = styled.div`
	touch-action: none;
	display: flex;
	flex-direction: column;
	color: ${({white}) => white ? '#fff' : '#3B3B3B'};
	width: 100%;
	background: ${({color}) => color};
	padding: 0 4px;
	border-radius: 2px;
	margin-bottom: 1px;
`;

const Row = styled.div`
	display: flex;
	justify-content: ${({spread}) => spread ? 'space-between' : 'initial'};
	padding: 7px 0 11px;

	+ div {
		border-top: 1px solid #ffffffab;
	}

	> span + span {
		border-color: ${({white}) => white ? '#fff' : '#3B3B3B'};
		border-left: ${({delimeter}) => delimeter ? '1px solid' : 'none'};
	}
`;

const RowEntry = styled.span`
	display: flex;
	align-items: flex-end;
	line-height: 1;
	letter-spacing: -.01em;
	color: ${({color}) => color ? color : 'inherit'};
	text-align: ${({align}) => align ? align : 'initial'};
	font-size: ${
    ({large, larger, medium}) => {
        if (large) return '18';
        if (larger) return '12';
        if (medium) return '10';
        return '9';
    }
}px;
	font-weight: ${({bold}) => bold ? '600' : '400'};
	padding-right: ${({padRight}) => padRight ? '6px' : 0};
	padding-left: ${({padLeft}) => padLeft ? '6px' : 0};
	text-overflow: ellipsis;
	white-space: nowrap;
	overflow: hidden;
`;

const Client = styled.div`
	background: rgba(255, 255, 255, 0.7);
	padding: 5px 15px;
	font-size: 9px;
	font-weight: 600;
	color: #3b3b3b;
`;

const Labels = styled.div`
	background: rgba(255, 255, 255, 0.7);
	padding: 5px 15px;
	font-size: 9px;
	font-weight: 600;
	color: #3b3b3b;
	${({margin}) => margin ? 'margin-left: 3px;' : ''};
`;

const Icon = styled.img`
	filter: ${({makeWhite}) => makeWhite
    ? 'brightness(0) invert(1)'
    : 'none'
};
`;

const allowedColors = {
    'normal': '#a6d9f8',
    'done': '#8fd5ba',
    'reserved': '#686871',
    'in-progress': '#b5aba3',
    'failed': 'repeating-linear-gradient(-55deg, #f43d3d, #f43d3d 15px, #f76d6d 10px, #f76d6d 30px)',
};

const allowedColorsDispo = {
    'normal': '#F8D06C',
    'done': 'repeating-linear-gradient(-55deg, #F8D06C, #F8D06C 10px, #8fd5ba 30px)',
    'reserved': 'repeating-linear-gradient(-55deg, #F8D06C, #F8D06C 10px, #686871 30px)',
    'in-progress': 'repeating-linear-gradient(-55deg, #F8D06C, #F8D06C 10px, #b5aba3 30px)',
    'failed': 'repeating-linear-gradient(-55deg, #f43d3d, #f43d3d 15px, #f76d6d 10px, #f76d6d 30px)',
};

const Item = ({task, handleClick, workstepcount, isDispo, noworkplace, allWorkSteps, extendtileinformation}) => {
    let localColors = allowedColors;
    if (isDispo && (R.isEmpty(task.departmentIds) || R.isNil(task.departmentIds))) {
        localColors = allowedColorsDispo;
    }

    const color = localColors[task.status] ? localColors[task.status] : localColors['normal'];
    const isWhite = ['reserved', 'failed'].indexOf(task.status) > -1;
    const reservedColor = task.status === 'reserved' ? localColors['reserved'] : null;

    let img = (task.status === 'done' && task.step === workstepcount);
    let workgroupallowed = task.workgroup !== null && task.workgroup !== '';

    let stepName = (getWorkstepLabel(allWorkSteps, task.step) !== '' ? getWorkstepLabel(allWorkSteps, task.step) + '-' : '') + getWorkstepName(allWorkSteps, task.step);

    let workerLabel = task.worker;
    let displayLastWorker = false;
    if (workerLabel === null || workerLabel === '') {
        if (!R.isEmpty(task.lastWorkerEntity) && !R.isNil(task.lastWorkerEntity)) {
            workerLabel = task.lastWorkerEntity;
            displayLastWorker = true;
        }
    }

    return (
        <Wrapper color={color} white={isWhite} onClick={handleClick} id={task.id}>
            <Row spread>
                <RowEntry bold large>
                    {task.order_quantity}

                    <RowEntry>&nbsp;Stk.&nbsp;
                        {task.owner &&
                        <FontAwesomeIcon size="lg" icon={faObjectGroup}/>
                        }
                    </RowEntry>

                </RowEntry>


                <RowEntry>
                    <RowEntry bold large>{task.erpnumber}</RowEntry>
                </RowEntry>
            </Row>

            {task.labels && task.labels.length > 0 &&
            <Row>
                {task.labels.map((label, index) =>
                    <Labels margin={index > 0} key={`col-${task.id}-label-${index}`}>{label}</Labels>
                )}
            </Row>}

            {extendtileinformation &&
            <Row delimeter>
                <RowEntry larger padRight>
                    {task.description}
                </RowEntry>
                <RowEntry larger padLeft>
                    {task.owningTaskDescription}
                </RowEntry>
            </Row>
            }

            <Row delimeter white={isWhite}>
                <RowEntry larger bold padRight>
                    {task.order_number}
                </RowEntry>

                <RowEntry larger bold padLeft>
                    {task.owningTaskErp}
                </RowEntry>
            </Row>

            {extendtileinformation &&
            <Row delimeter>
                <RowEntry larger bold padRight>
                    {task.timea ? task.timea + 'h' : '-'}
                </RowEntry>
                <RowEntry larger bold padLeft padRight>
                    {task.timeb ? task.timeb + 'h' : '-'}
                </RowEntry>
                <RowEntry larger bold padLeft>
                    {task.timec ? task.timec + 'h' : '-'}
                </RowEntry>
            </Row>
            }

            <Row>
                <Client>{task.order_client}</Client>
            </Row>

            {!noworkplace && <Row delimeter white={isWhite}>
                <RowEntry padRight>
                    <RowEntry bold>{task.quantity}&nbsp;</RowEntry>

                    von

                    <RowEntry bold>&nbsp;{task.originalQuantity} Stk.</RowEntry>
                </RowEntry>

                <RowEntry padLeft padRight>
                    Platz <RowEntry bold>&nbsp;{task.place ? task.place : '-'}</RowEntry>
                </RowEntry>

                <RowEntry padLeft>
                    Schritt

                    <RowEntry bold>&nbsp;{task.step !== null ? task.step : '0'}&nbsp;</RowEntry>

                    von

                    <RowEntry bold>&nbsp;{workstepcount}</RowEntry>
                </RowEntry>

            </Row>}

            {noworkplace && <Row delimeter white={isWhite}>
                <RowEntry padRight>
                    <RowEntry bold>{task.quantity}&nbsp;</RowEntry>

                    von

                    <RowEntry bold>&nbsp;{task.originalQuantity} Stk.</RowEntry>
                </RowEntry>

                <RowEntry padLeft>
                    {ts_translate('Arbeitsschritt')}:
                    <RowEntry larger bold>&nbsp;{stepName}&nbsp;</RowEntry>
                </RowEntry>

            </Row>}

            <Row spread>
                <RowEntry larger>
                    {!workgroupallowed &&
                    (displayLastWorker
                            ? <FontAwesomeIcon size="1x" color={'#fff'} icon={faUserClock}/>
                            : <FontAwesomeIcon size="1x" color={'#fff'} icon={faUser}/>
                    )
                    }

                    {workgroupallowed &&
                    <FontAwesomeIcon size="1x" color={'#fff'} icon={faUsers}/>}

                    &nbsp;{workerLabel}
                </RowEntry>

                <RowEntry larger bold>
                    {ts_translate('L. Datum:')} {moment(task.order_deadline).format('DD.MM.')}
                </RowEntry>
            </Row>
        </Wrapper>
    );
};

export default Item



// WEBPACK FOOTER //
// ./src/components/dashboard/grid/item.js